/**
 * Hugin's waiting proposals on the timetable (V19 slice 5, owner 2026-10-08). The server holds
 * them (`/poc/actions`, poc-chat/actions.ts) and is the only source: nothing here is stored, and
 * accepting one confirms that action id, the exact change the grid showed. A proposal is drawn
 * as "Forslag · ikke lagret" (GLOSSARY) until confirmed.
 *
 * Only the kinds that are shifts in a cell are drawn: a new shift (create), a moved one (move)
 * and drafts to publish (publish). Everything else stays in the chat and is counted.
 */
import { T } from '@/i18n';
import type { ActionCard } from '@/poc-chat/actions';

/** One proposed shift drawn in a cell. */
export interface Ghost {
  actionId: string;
  kind: 'create' | 'move';
  /** the row it belongs to: a user id, or null for the open row */
  userId: number | null;
  date: string;
  start: string;
  end: string;
  /** the shift it moves, for a move */
  shiftId?: number;
}

export interface OnGrid {
  ghosts: Ghost[];
  /** existing shifts a proposal touches, and how: moved away, or published */
  marks: Map<number, { actionId: string; what: 'moves' | 'publishes' }>;
  /** the action ids drawn on the grid, in the server's order */
  gridActions: string[];
  /** proposals that are not shifts in a cell, still waiting in the chat */
  elsewhere: number;
  /** entries whose person could not be found among the rows, so they are not drawn */
  unplaced: number;
  /** per proposal, how many of its entries fall outside what is on screen (other dates, or filtered away) */
  outside: Map<string, number>;
}

type Shiftish = { id: number; user_id: number | null; start: string; date: string; published: boolean };

export function onGrid(
  pending: ActionCard[],
  people: { id: number; name: string }[],
  shifts: Shiftish[],
  days: string[],
  /** the rows on screen; a proposal for anybody else is outside the view. Omitted: everybody */
  shown?: Set<number>,
): OnGrid {
  const byName = new Map(people.map((p) => [p.name.trim().toLocaleLowerCase('nb'), p.id]));
  const who = (name: string | null): number | null | undefined =>
    name == null ? null : byName.get(name.trim().toLocaleLowerCase('nb'));
  const out: OnGrid = {
    ghosts: [],
    marks: new Map(),
    gridActions: [],
    elsewhere: 0,
    unplaced: 0,
    outside: new Map(),
  };
  const off = (id: string, yes: boolean) => yes && out.outside.set(id, (out.outside.get(id) ?? 0) + 1);
  const seen = (u: number | null) => u == null || !shown || shown.has(u);
  for (const c of pending) {
    if (c.kind === 'move') {
      const s = shifts.find((x) => x.id === c.shift_id);
      out.gridActions.push(c.action_id);
      out.marks.set(c.shift_id, { actionId: c.action_id, what: 'moves' });
      off(c.action_id, !days.includes(c.to.date) || !seen(s?.user_id ?? null));
      if (days.includes(c.to.date))
        out.ghosts.push({
          actionId: c.action_id,
          kind: 'move',
          userId: s?.user_id ?? null,
          date: c.to.date,
          start: c.to.start,
          end: c.to.end,
          shiftId: c.shift_id,
        });
    } else if (c.kind === 'create') {
      out.gridActions.push(c.action_id);
      for (const e of c.entries) {
        // the person's id when the list carries it; a name only for an older proposal without one
        const u = e.user_id !== undefined ? e.user_id : who(e.employee);
        off(c.action_id, !days.includes(e.date) || (u !== undefined && !seen(u)));
        if (u === undefined) out.unplaced++;
        else if (days.includes(e.date))
          out.ghosts.push({
            actionId: c.action_id,
            kind: 'create',
            userId: u,
            date: e.date,
            start: e.start,
            end: e.end,
          });
      }
    } else if (c.kind === 'publish') {
      out.gridActions.push(c.action_id);
      for (const e of c.entries) {
        const u = who(e.employee);
        const s = shifts.find(
          (x) => !x.published && x.user_id === (u ?? null) && x.date === e.date && x.start === e.start,
        );
        if (s) out.marks.set(s.id, { actionId: c.action_id, what: 'publishes' });
        else out.unplaced++;
      }
    } else out.elsewhere++;
  }
  return out;
}

/** What one proposal changes, a line each, for the panel: the same facts the chat card shows. */
export function linesOf(c: ActionCard): string[] {
  const at = (date: string, start: string, end: string) =>
    `${date.slice(8)}.${date.slice(5, 7)} ${start}–${end}`;
  if (c.kind === 'move')
    return [
      `${c.employee ?? T('Ledig vakt')}: ${c.from.date ? at(c.from.date, c.from.start, c.from.end) : `${c.from.start}–${c.from.end}`} → ${at(c.to.date, c.to.start, c.to.end)}`,
    ];
  if (c.kind === 'create' || c.kind === 'publish')
    return c.entries.map((e) => `${e.employee ?? T('Ledig vakt')}: ${at(e.date, e.start, e.end)}`);
  return [];
}
